import React, { useState, useEffect, useRef, useCallback } from "react";
import useAxios from "../../hooks/useAxios";
import useCommonActions from "../../hooks/useCommonActions";
import { ContentLoader, PageToolbar, Pagination, TooltipWrapper } from "../common";
import badges from "../../_helpers/badgeHelper";
import helpers from "../../_helpers/common";
import { hasAuthorization } from "../../_helpers/hasAuthorization";
import SearchForm from "./partial/SearchForm";
import CreateUpdate from "./CreateUpdate";
import CommonList from "../common/CommonList";
import { CouponSearchFormState, Coupon } from "../../types/interfaces";

const List: React.FC = () => {
  const axiosInstance = useAxios();
  const { changeItemStatus, deleteItem } = useCommonActions();
  const [searchForm, setSearchForm] = useState<CouponSearchFormState>({ search_string: "", status: "" });
  const [results, setResults] = useState<Coupon[] | null>(null);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10 });
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [modalopen, setModalOpen] = useState(false);
  const [selectedcoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const isInitialRender = useRef(false);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    getListData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);

  /*Handle pagination data change*/
  const handlePageChange = (page: number, pageSize: number) => {
    setPagination({ currentPage: page, pageSize });
  };

  const getListData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: any = await axiosInstance({
        url: "coupons/list",
        method: "GET",
        data: {
          page: pagination.currentPage,
          limit: pagination.pageSize,
          ...searchForm,
        },
      });
      // console.log('event interest list response', response);
      setResults(response.data.coupons.docs);
      setTotalItems(response.data.coupons.totalDocs);
    } catch (error: any) {
      console.error("Error in api request:", error);
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[pagination,searchForm]);

  /*Change selected item status*/
  const changeCouponStatus = async (itemid?: string, status?: number) => {
    const newStatus = status === 1 ? 0 : 1;
    const message = `Are you sure you want to ${
      status === 1 ? "deactivate" : "activate"
    } the Coupon?`;
    changeItemStatus(
      itemid,
      newStatus,
      "coupons/update-status",
      message,
      getListData
    );
  };

  /*Delete selected item*/
  const deleteCoupon = async (itemid?: string) => {
    const message = `Are you sure you want to delete the coupon?`;
    deleteItem(`coupons/delete/${itemid}`, message, getListData);
  };
  
  const handleEditCoupon = (selectedcoupon: Coupon) => {
	  setSelectedCoupon(selectedcoupon);
	  setModalOpen(true);
  }

  return (
    <>
      <div className="d-flex justify-content-between">
        {/*Page toolbar Area Start*/}
        <PageToolbar title="Coupon List" />
        <button	className="btn btn-flex btn-primary h-40px fs-7 fw-bold" onClick={() => setModalOpen(true)}>
			Create Coupon
		</button>
      </div>
	  <div id="kt_content" className="content flex-column-fluid">
        {/*Search Area Start*/}
        <SearchForm searchForm={searchForm} setSearchForm={setSearchForm} handleSearchClick={getListData}/>
        {/*Search Area End*/}

        {/*Content Area Start*/}
        <div className={`card ${isLoading ? "overlay overlay-block" : ""}`}  style={{ minHeight:"70vh", maxHeight: "70vh" }} >
          <div className="card-body pt-0 pb-0 table-responsive">
		    <table className="table table-hover table-row-bordered table-row-solid gy-4 align-middle fw-bold w-100">
			  <thead
				style={{
				  position: "sticky",
				  top: 0,
				  zIndex: 5,
				  backgroundColor: `${isLoading ? "#BBBCBE" : "white"}`,
				  boxShadow: "0 4px 2px -2px rgba(190, 190, 190, 0.2)",
				}}
			  >
				<tr className="text-start text-gray-800 fw-bold fs-7 text-uppercase gs-0">
				 
				  <th className="min-w-150px">Coupon Code</th>
				  <th className="min-w-100px">Discount</th>
				  <th className="min-w-150px">Max Discount</th>
				  <th className="min-w-150px">Min Cart Value</th>
				  <th className="min-w-100px">Usage Limit</th>
				  <th className="min-w-100px">Start Date</th>
				  <th className="min-w-100px">End Date</th>
				  <th className="min-w-70px">Status</th>
				  <th className="min-w-150px">Created Date</th>
				  <th className="min-w-70px">Actions</th>
				</tr>
			  </thead>
			  <tbody className="fw-semibold text-gray-600">
				{!results || results.length === 0 ? (
				  <tr className="text-center">
					<td colSpan={10}>No Content Found</td>
				  </tr>
				) : (
				  results.map((item) => (
					<tr key={item._id}>
					  <td>{item?.code || "N/A"}</td>
					  <td>
						  {item?.type === "fixed" ? "Rs. " : ""}
						  {item?.value ?? "N/A"}
						  {item?.type === "percentage" ? "%" : ""}
					  </td>
					  <td>{item?.max_discount || "N/A"}</td>
					  <td>{item?.min_cart_value || "N/A"}</td>
					  <td>{item?.usage_limit || "N/A"}</td>
					  <td>{helpers.convertDateTime(item?.start_date, "DD MMM YYYY") || "N/A"}</td>
					  <td>{helpers.convertDateTime(item?.end_date, "DD MMM YYYY") || "N/A"}</td>
					  
					  <td>
						{badges.eventStatusBadge(
						  item.status ?? 0,
						  item?.statusname ?? ""
						)}
					  </td>
					  <td>
						{helpers.convertDateTime(
						  item?.created_at,
						  "DD MMM YYYY"
						) || "N/A"}
					  </td>
					  <td className="text-end">
						<div className="d-flex justify-content-start">
						  {hasAuthorization("edit", "coupon") && (
							<TooltipWrapper title="Edit" placement="top">
							  <span
								className="btn btn-sm btn-icon btn-light btn-active-light-warning me-2"
								onClick={() =>
								  handleEditCoupon(item)
								}
							  >
								<i className="ki-outline ki-notepad-edit text-warning fs-2"></i>
							  </span>
							</TooltipWrapper>
						  )}

						  {hasAuthorization("update_status", "coupon") && (
							<TooltipWrapper
							  title="Update Coupon Status"
							  placement="top"
							>
							  <span
								className="btn btn-sm btn-icon btn-light btn-active-light-warning me-2"
								onClick={() =>
								  changeCouponStatus(item?._id, item?.status)
								}
							  >
								<i className="ki-outline ki-shield-tick text-warning fs-2"></i>
							  </span>
							</TooltipWrapper>
						  )}

						  {hasAuthorization("delete", "coupon") && (
							<TooltipWrapper title="Delete Coupon" placement="top">
							  <span
								className="btn btn-sm btn-icon btn-light btn-active-light-danger"
								onClick={() => deleteCoupon(item?._id)}
							  >
								<i className="ki-outline ki-trash text-danger fs-2"></i>
							  </span>
							</TooltipWrapper>
						  )}
						</div>
					  </td>
					</tr>
				  ))
				)}
			  </tbody>
			  <tfoot></tfoot>
			</table>
		  
		  </div>
          {/*Content Area End*/}
          {/*Pagination Start*/}
          <div
            className={`sticky-bottom bg-white border rounded p-2 shadow-sm ${
              isLoading ? "" : "border-top"
            }`}
            style={{
              bottom: 0,
              zIndex: 2,
              // backgroundColor: `${isLoading ? "#BBBCBE" : "white"}`,
            }}
          >
            {results && results.length > 0 ? (
              <Pagination
                totalItems={totalItems}
                itemsPerPage={pagination.pageSize}
                onPageChange={handlePageChange}
              />
            ) : null}
          </div>
          {/*Pagination End*/}

          {/*Content Loader Start*/}
          {isLoading ? <ContentLoader /> : null}
          {/*Content Loader End*/}
        </div>
		{ modalopen && (
			<CreateUpdate
			  show={modalopen}
			  onClose={() => setModalOpen(false)}
			  onSuccess={getListData}
			  editData={selectedcoupon}
			/>
		)}
      </div>
   </>
  );
};

export default List;
